// Facts about a call itself — who it was with, when, how long, how many people
// — shaped for display. Shared by the call history and the call card so the
// two never describe the same call differently.

// "4m 05s", "52s". Seconds matter on short calls: the median call here is
// under two minutes, and "2 min" for a 101-second call is a fifth off.
export function formatDuration(seconds) {
  const s = Math.round(Number(seconds));
  if (!Number.isFinite(s) || s <= 0) return null;
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = String(s % 60).padStart(2, "0");
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m ${rest}s`;
}

// Local wall-clock start, e.g. "10:02 am". Null when the call has no start
// time (hand-entered rows, and scored rows from before it was recorded).
export function formatStartTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function formatDate(call) {
  const raw = call.started_at || call.call_date;
  if (!raw) return "undated";
  const d = new Date(call.started_at || `${call.call_date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return call.call_date || "undated";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function durationOf(call) {
  return call.duration_seconds ?? call.call_context?.duration_seconds ?? null;
}

export function companyOf(call) {
  return call.company?.name || call.company_name || "";
}

// Two calls are with the same company when they point at the same CRM lead, or (no lead on
// one of them) carry the same name apart from capitals and stray spaces.
export function sameCompany(a, b) {
  const idA = a?.company?.id;
  const idB = b?.company?.id;
  if (idA != null && idB != null && idA === idB) return true;
  const nameA = companyOf(a).trim().toLowerCase();
  return nameA !== "" && nameA === companyOf(b).trim().toLowerCase();
}

// "3 Oct". `empty` is what to show when there is no date; callers that join pieces of text
// leave it blank, a screen that must say something passes its own word.
export function shortDate(iso, empty = "") {
  if (!iso) return empty;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function plural(n, word) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export function participantsOf(call) {
  return call.call_context?.participant_count ?? null;
}

// Why a call carries no scores, in words a rep can accept. The server's own
// message is the precise version and is shown beneath this on the card.
const REFUSAL_LABELS = {
  too_short: "Too short to score",
  empty_transcript: "No speech recorded",
  one_sided: "Only one side recorded",
  rep_barely_spoke: "You barely spoke on it",
  no_exchange: "No real back-and-forth",
  speakers_not_separated: "Voices couldn't be told apart",
};

// What kind of phone call it was. ECHO works this out from the lead's history,
// the opening minute, or the scorer's own read; "Cold call / prospecting" was
// printed on every phone call, callbacks included.
const CALL_KIND_LABELS = {
  cold: "Cold call",
  callback: "Callback",
  follow_up: "Follow-up call",
};

export function callTypeLabel(call) {
  if (call.modality === "call") {
    const kind = call.call_context?.call_kind;
    return CALL_KIND_LABELS[kind] || "Phone call";
  }
  return call.modality_label || call.modality;
}

export function isRefused(call) {
  return call.scoreable === false;
}

export function refusalLabel(call) {
  const code = call.refusal?.code;
  return REFUSAL_LABELS[code] || "Not scored";
}

// The observed-parameter average, or null. The one place both the history
// row and anything else that summarises a call get this number from.
export function callAverage(call) {
  const obs = (call.parameters || []).filter((p) => p.observed !== false && typeof p.score === "number");
  if (!obs.length) return { avg: null, count: 0 };
  return {
    avg: Math.round((obs.reduce((a, p) => a + p.score, 0) / obs.length) * 10) / 10,
    count: obs.length,
  };
}

// A score at or below this is a MISS: the line Patterns, Growth, the call card
// and the backend (SellerCallMistake.MISS_THRESHOLD) all count by.
export const MISS_AT = 5;

// The score a skill is working towards: at or above it is on target. Every panel's target line, its
// "what a 7 is" copy and the green end of the colour scale come from this one number.
export const TARGET = 7;

// One colour scale for every score on every screen, and it follows the miss
// line. It used to be green 7+, blue 4-6, red under 4 -- so a 4.3 was painted
// the same calm blue as a 6.8 ("fine") while the page beside it counted that
// 4.3 as a miss and listed what pulls it down. Amber is the miss the rep can
// fix this week; red is the one to fix first.
export function scoreBand(score) {
  if (score == null) return null;
  if (score >= TARGET) return { key: "good", label: "Good", color: "var(--green-600)" };
  if (score > MISS_AT) return { key: "close", label: "Close", color: "var(--blue-600)" };
  if (score >= 4) return { key: "miss", label: "Needs work", color: "var(--amber-600)" };
  return { key: "focus", label: "Fix this first", color: "var(--red-600, #c0392b)" };
}

export function scoreColor(avg) {
  return avg == null ? "var(--c-400)" : scoreBand(avg).color;
}
